import React, { useState, useEffect, useCallback } from 'react';
import EmptyState from '../../components/EmptyState';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Stack,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Campaign as CampaignIcon,
  PriorityHigh as UrgentIcon,
  NotificationImportant as ImportantIcon,
  NotificationsNone as NormalIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { teacherAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const PRIORITY_CONFIG = {
  normal: { label: 'Normal', color: 'default', icon: <NormalIcon fontSize="small" />, borderColor: 'divider' },
  important: { label: 'Important', color: 'warning', icon: <ImportantIcon fontSize="small" />, borderColor: 'warning.light' },
  urgent: { label: 'Urgent', color: 'error', icon: <UrgentIcon fontSize="small" />, borderColor: 'error.light' }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(dateStr);
};

const TeacherAnnouncements = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const fetchAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      const res = await teacherAPI.getAnnouncements();
      setAnnouncements(res.data.announcements || []);
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to load announcements', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">Announcements</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Notices and updates from your school administration
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/teacher/dashboard')}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Back to Dashboard
        </Button>
      </Box>

      {announcements.length === 0 ? (
        <EmptyState
          icon={CampaignIcon}
          title="No announcements yet"
          description="Your school administration hasn't posted any announcements."
        />
      ) : (
        <Grid container spacing={2}>
          {announcements.map((a) => {
            const pc = PRIORITY_CONFIG[a.priority] || PRIORITY_CONFIG.normal;
            return (
              <Grid item xs={12} md={6} lg={4} key={a.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid',
                    borderColor: pc.borderColor,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}
                >
                  <CardContent sx={{ flex: 1 }}>
                    <Stack direction="row" alignItems="center" mb={1.5}>
                      <Chip
                        icon={pc.icon}
                        label={pc.label}
                        color={pc.color}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>

                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {a.title}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {a.content}
                    </Typography>
                  </CardContent>

                  <Divider />

                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="caption" color="text.disabled">
                      Posted by {a.author?.name || 'Administration'} · {timeAgo(a.created_at)}
                    </Typography>
                    {a.expires_at && (
                      <Typography variant="caption" color="text.disabled" display="block">
                        Expires {formatDate(a.expires_at)}
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TeacherAnnouncements;
